/**
 * 지원 상태 — 서버가 쓰는 값 그대로.
 *
 * 어드민 화면은 전에 '단계(지원/1차/2차/최종) × 결과(검토중/합격/탈락)'라는
 * 자체 모델을 썼다. 서버에는 그런 필드가 없고 지원 하나에 status 하나만 있어서,
 * 모든 지원자가 '지원' 칸에 모이고 단계를 바꿔도 반영되지 않았다.
 * (배우 마이페이지·제작사 지원자 관리는 처음부터 이 enum을 쓴다.)
 *
 * 두 축을 하나로 합치면 '1차인데 탈락'처럼 서버에 없는 상태를 만들 수 없게 된다.
 */
export const APPLICANT_STATUSES = [
  { key: 'SUBMITTED', label: '지원 완료', short: '지원',    color: '#555555', cls: 'ag-badge--warn',    base: true },
  { key: 'PASS_1',    label: '1차 통과',  short: '1차',     color: '#185FA5', cls: 'ag-badge--success', base: true },
  { key: 'PASS_2',    label: '2차 통과',  short: '2차',     color: '#BA7517', cls: 'ag-badge--success', base: true },
  { key: 'PASS_3',    label: '3차 통과',  short: '3차',     color: '#7F77DD', cls: 'ag-badge--success' },
  { key: 'PASS_4',    label: '4차 통과',  short: '4차',     color: '#993556', cls: 'ag-badge--success' },
  { key: 'HIRED',     label: '최종 합격', short: '최종',    color: '#1D9E75', cls: 'ag-badge--success', base: true },
  { key: 'REJECTED',  label: '탈락',      short: '탈락',    color: '#D4537E', cls: 'ag-badge--danger' },
  { key: 'WITHDRAWN', label: '지원 취소', short: '취소',    color: '#999999', cls: 'ag-badge--gray' },
];

const BY_KEY = Object.fromEntries(APPLICANT_STATUSES.map(s => [s.key, s]));

/**
 * 지원자 응답에서 상태를 읽는다.
 *
 * 응답에 함께 오는 stage/result는 읽지 않는다. 실제 상태와 연결이 없던 시절의
 * 값이 남아 있어, 같은 지원자가 status는 PASS_2인데 stage는 applied인 경우가 있다.
 * 그 값을 읽으면 통과한 지원자가 '지원' 칸으로 되돌아가 보인다.
 *
 * status가 비어 있는 과거 데이터는 '지원 완료'로 본다 — 서버는 추측해서 채우지
 * 않고 null을 그대로 보내지만, 화면에는 무언가를 세워야 한다.
 */
export function readApplicantStatus(applicant) {
  const raw = applicant?.status;
  if (typeof raw === 'string') {
    const key = raw.trim().toUpperCase();
    if (BY_KEY[key]) return key;
  }
  return 'SUBMITTED';
}

/** 화면 표시용 정보. 모르는 값이 와도 라벨 자리를 비우지 않는다. */
export function statusMeta(key) {
  return BY_KEY[key] ?? { key, label: key ?? '—', short: key ?? '—', color: '#999999', cls: 'ag-badge--gray' };
}

/**
 * 상태 변경 요청 본문.
 *
 * status만 보낸다. 예전 stage/result를 함께 보내면 서버가 그 값을 쓰는 옛 경로로
 * 빠지는데, 그 경로는 실제 지원 상태를 바꾸지 않아 제작사·배우 화면과 어긋난다.
 *
 * message는 지원 이력에 남고 배우에게 알림으로 간다. 어드민 메모(memo)와 달리
 * 배우가 읽으므로, 비워서 보내지 않는다.
 */
export function statusPatch(key, message) {
  const text = message?.trim();
  return { status: key, ...(text ? { message: text } : {}) };
}

/**
 * 더 바꿀 수 없는 상태.
 * 서버가 거부한다 — 합격·탈락·취소는 지원이 끝난 상태다.
 */
export function isTerminal(key) {
  return key === 'HIRED' || key === 'REJECTED' || key === 'WITHDRAWN';
}

/**
 * 고를 수 있는 상태.
 *
 * 지원 취소(WITHDRAWN)는 배우 본인만 할 수 있어 어드민이 보내면 서버가 거부한다.
 * 다만 이미 취소된 지원은 그 상태로 보여야 하므로, 지금 값이면 목록에 남기되 못 고르게 한다.
 */
export function selectableStatuses(current) {
  return APPLICANT_STATUSES
    .filter(s => s.key !== 'WITHDRAWN' || current === 'WITHDRAWN')
    .map(s => ({ ...s, disabled: s.key === 'WITHDRAWN' }));
}

/**
 * 칸반에 세울 칸.
 *
 * 기본 네 칸(지원·1차·2차·최종)은 비어 있어도 세운다 — 다음 단계가 어디인지 보여야 한다.
 * 3·4차와 탈락·취소는 해당하는 지원자가 있을 때만 세운다. 늘 세우면 쓰지 않는
 * 공고에서도 여덟 칸이 늘어서 좁아진다.
 */
export function visibleStatuses(statusKeys) {
  const present = new Set(statusKeys);
  return APPLICANT_STATUSES.filter(s => s.base || present.has(s.key));
}
